import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import type { LightboxItem } from './lightbox-captions'
import { InlineAudioPlayer } from '@shared/media/audio/InlineAudioPlayer'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  LmsButton,
} from '@shared/ui'
import './image-lightbox.css'

export function ImageLightbox({
  activeIndex,
  items,
  labels,
  onChange,
  onClose,
  open,
}: {
  readonly activeIndex: number
  readonly items: LightboxItem[]
  readonly labels?: {
    readonly close: string
    readonly previous: string
    readonly next: string
    readonly audio: { readonly play: string; readonly stop: string; readonly unavailable: string }
  }
  readonly onChange: (nextIndex: number) => void
  readonly onClose: () => void
  readonly open: boolean
}) {
  const activeItem = items[activeIndex] ?? null

  if (!activeItem) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        onClose()
      }
    }}>
      <DialogContent
        className="imageLightbox left-0 top-0 h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 overflow-hidden rounded-none border-0 p-2 sm:p-4"
        hideClose
      >
        <div className="imageLightboxInner">
          <div className="imageLightboxHeader">
            <div className="imageLightboxHeading">
              <DialogTitle className="imageLightboxTitle">
                {activeItem.title}
              </DialogTitle>
              {activeItem.subtitle ? (
                <DialogDescription className="imageLightboxSubtitle">
                  {activeItem.subtitle}
                </DialogDescription>
              ) : null}
            </div>
            <div className="imageLightboxActions">
              <InlineAudioPlayer audio={activeItem.audio} labels={labels?.audio} />
              <LmsButton
                aria-label={labels?.close ?? 'Close slideshow'}
                className="imageLightboxControl"
                onClick={onClose}
                size="icon"
                variant="ghost"
              >
                <X aria-hidden="true" className="size-5" />
              </LmsButton>
            </div>
          </div>

          <div className="imageLightboxStage">
            <LmsButton
              aria-label={labels?.previous ?? 'Show previous image'}
              className="imageLightboxControl"
              disabled={activeIndex <= 0}
              onClick={() => {
                onChange(activeIndex - 1)
              }}
              size="icon"
              variant="ghost"
            >
              <ArrowLeft aria-hidden="true" className="size-5" />
            </LmsButton>

            <img
              alt={activeItem.title}
              className="imageLightboxImage"
              src={activeItem.imageUrl}
            />

            <LmsButton
              aria-label={labels?.next ?? 'Show next image'}
              className="imageLightboxControl"
              disabled={activeIndex >= items.length - 1}
              onClick={() => {
                onChange(activeIndex + 1)
              }}
              size="icon"
              variant="ghost"
            >
              <ArrowRight aria-hidden="true" className="size-5" />
            </LmsButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
